/**
 * The company details the block prints — fields of the site's own `jss_general` row with
 * its `contactOverrides` applied. The templates name these seven and pass nothing else:
 * the row itself is the site's whole record (meta tags, the store url, a fax number) and
 * an address block has no use for the rest of it.
 */
export type Company = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  generalemail?: string;
  telephone?: string;
};

export type ContactInfoProps = {
  company: Company;
  /**
   * Which of the three pieces this site publishes — any of `address`, `email` and
   * `telephone`. It is the `displayContact` list an administrator ticks per site under
   * adminmagz > General > Company, and it reaches a template as
   * `contactform.displayContactInfo`.
   */
  displayContactInfo: string[];
};

/**
 * The company's postal address, email and telephone, each shown only if the site says to.
 *
 * This is `includes/modules/contact/address.html`, which MagSpot's and MagZoom's legacy
 * contact pages closed with. Shared rather than written out on each of them because both
 * brands' copies of that include were identical, and because the gate is a rule rather
 * than copy: it is the administrator's list that decides, and getting the check wrong
 * publishes an address a site chose not to publish.
 *
 * All three are off on both brands today, so this renders nothing — but it renders
 * nothing rather than an empty `<address>`, which is what the legacy include left behind.
 * Ticking a box in adminmagz is all it should take to bring the block back.
 */
export default function ContactInfo({ company, displayContactInfo }: ContactInfoProps) {
  const showAddress = displayContactInfo.includes('address');
  const showEmail = displayContactInfo.includes('email');
  const showTelephone = displayContactInfo.includes('telephone');

  if (!showAddress && !showEmail && !showTelephone) {
    return null;
  }

  return (
    // `not-italic` because a browser italicises `<address>` by default and the legacy
    // page did not — Bootstrap reset it, and there is no Bootstrap here.
    <address className="not-italic">
      {showAddress && (
        <span className="block">
          {company.addressLine1}
          <br />
          {company.addressLine2 && (
            <>
              {company.addressLine2}
              <br />
            </>
          )}
          {company.city}, {company.county} {company.postcode}
        </span>
      )}

      {showEmail && <span className="block">{company.generalemail}</span>}

      {showTelephone && <span className="block">{company.telephone}</span>}
    </address>
  );
}
